import { useId, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import type { TaskTypeViewMatchTarget } from '@erp/shared-types';
import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import {
  optionValue,
  pathFromOptionValue,
  withCurrentPath,
  type ViewFieldOption,
} from './taskTypeViewModel';

/**
 * A schema-path select. `missingLabel` names a saved path the options do not
 * contain; `trailing` adds one non-path item after the options (for example
 * "Custom path…") whose pick is reported through `onTrailing`.
 */
export function ViewPathSelect({
  options,
  path,
  disabled,
  onChange,
  missingLabel,
  trailing,
  onTrailing,
}: {
  options: readonly ViewFieldOption[];
  path: readonly string[];
  disabled?: boolean;
  onChange: (path: string[]) => void;
  missingLabel?: (path: string) => string;
  trailing?: { value: string; label: ReactNode };
  onTrailing?: () => void;
}) {
  const gt = useGT();
  const choices = withCurrentPath(options, path);
  const missing =
    missingLabel ??
    ((label: string) =>
      gt('Missing field ({path})', {
        context:
          'task page builder option when a saved path is not on the schema',
        path: label,
      }));
  return (
    <Select
      value={optionValue(path)}
      disabled={disabled || choices.length === 0}
      onValueChange={(value) => {
        if (trailing && value === trailing.value) {
          onTrailing?.();
          return;
        }
        const next = pathFromOptionValue(value);
        if (next) onChange(next);
      }}
    >
      <SelectTrigger
        aria-label={gt('Field', {
          context: 'task page builder, which schema field this item shows',
        })}
        className="w-56"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {choices.map((option) => (
            <SelectItem
              key={optionValue(option.path)}
              value={optionValue(option.path)}
            >
              {option.missing ? missing(option.label) : option.label}
            </SelectItem>
          ))}
          {trailing ? (
            <SelectItem value={trailing.value}>{trailing.label}</SelectItem>
          ) : null}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

const NO_SUPPLIER_VALUE = '__none__';

/**
 * Supplier field for a supplier-line remap. None means the picker searches
 * the catalog only and the rows are never matched again on the page.
 */
export function ViewSupplierPathSelect({
  options,
  path,
  disabled,
  onChange,
}: {
  options: readonly ViewFieldOption[];
  path: readonly string[] | null;
  disabled?: boolean;
  onChange: (path: string[] | null) => void;
}) {
  const gt = useGT();
  const choices = path ? withCurrentPath(options, path) : [...options];
  return (
    <Select
      value={path ? optionValue(path) : NO_SUPPLIER_VALUE}
      disabled={disabled}
      onValueChange={(value) => {
        if (value === NO_SUPPLIER_VALUE) {
          onChange(null);
          return;
        }
        const next = pathFromOptionValue(value);
        if (next) onChange(next);
      }}
    >
      <SelectTrigger
        aria-label={gt('Supplier field', {
          context:
            'task page builder, which schema field holds the supplier for a supplier-line remap',
        })}
        className="w-56"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={NO_SUPPLIER_VALUE}>
            <T context="task page builder, supplier-line remap without a supplier field">
              No supplier (catalog only)
            </T>
          </SelectItem>
          {choices.map((option) => (
            <SelectItem
              key={optionValue(option.path)}
              value={optionValue(option.path)}
            >
              {option.missing
                ? gt('Missing field ({path})', {
                    context:
                      'task page builder option when a saved path is not on the schema',
                    path: option.label,
                  })
                : gt('Supplier: {label}', {
                    context:
                      'task page builder, supplier field option for a supplier-line remap',
                    label: option.label,
                  })}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export function ViewModeToggle({
  mode,
  disabled,
  onChange,
}: {
  mode: 'read' | 'edit';
  disabled?: boolean;
  onChange: (mode: 'read' | 'edit') => void;
}) {
  const gt = useGT();
  return (
    <ToggleGroup
      type="single"
      value={mode}
      disabled={disabled}
      aria-label={gt('Whether the person can edit this value', {
        context: 'task page builder read or edit toggle',
      })}
      onValueChange={(value) => {
        if (value === 'read' || value === 'edit') onChange(value);
      }}
    >
      <ToggleGroupItem value="read" className="h-8 px-2.5 text-xs">
        <T context="task page builder, show the value without editing">Read</T>
      </ToggleGroupItem>
      <ToggleGroupItem value="edit" className="h-8 px-2.5 text-xs">
        <T context="task page builder, the person can correct the value">
          Edit
        </T>
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export function ViewMatchTargetToggle({
  target,
  disabled,
  onChange,
}: {
  target: TaskTypeViewMatchTarget;
  disabled?: boolean;
  onChange: (target: TaskTypeViewMatchTarget) => void;
}) {
  const gt = useGT();
  return (
    <ToggleGroup
      type="single"
      value={target}
      disabled={disabled}
      aria-label={gt('What this match searches', {
        context: 'task page builder match target toggle',
      })}
      onValueChange={(value) => {
        if (value === 'supplier' || value === 'product_variant')
          onChange(value);
      }}
    >
      <ToggleGroupItem value="supplier" className="h-8 px-2.5 text-xs">
        <T context="task page builder match target, a supplier">Supplier</T>
      </ToggleGroupItem>
      <ToggleGroupItem value="product_variant" className="h-8 px-2.5 text-xs">
        <T context="task page builder match target, a product variant">
          Product
        </T>
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export function ViewLabelInput({
  label,
  disabled,
  onChange,
}: {
  label: string | null;
  disabled?: boolean;
  onChange: (label: string) => void;
}) {
  const gt = useGT();
  const id = useId();
  return (
    <Input
      id={id}
      value={label ?? ''}
      disabled={disabled}
      className="w-40"
      placeholder={gt('Label', {
        context: 'placeholder for an optional task page item label',
      })}
      aria-label={gt('Label override', {
        context:
          'task page builder optional label, blank uses the schema label',
      })}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
